import React, { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '../../../../../base-ui/Grid';
import { AddDesignPackageModal } from '../modalContent/AddDesignPackageModal';
import { DeleteDesignPackageModal } from '../modalContent/DeleteDesignPackageModal';
import { EditDesignPackageModal } from '../modalContent/EditDesignPackageModal';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ConfigContext } from '../../../../../base-ui/lib';
import { GridActionsCellItem } from '@mui/x-data-grid';
import PermitGridToolbar from './PermitGridToolbar';
import EmptySearchState from '../../../../../base-ui/Grid/components/EmptySearchState';
import { useCompanyState } from '../../../../../stateProviders/CompanyStateProvider';

const EmptyStateComponent = () => <EmptySearchState />;

const AnyGrid = Grid as any;

export const DesignPackageGrid = () => {
    const { config }: any = React.useContext(ConfigContext);
    const companyState = useCompanyState();
    const gridRef = React.useRef<any>(null);

    const logisticCostsId = companyState.logisticCosts?._id;
    const [addModalOpen, setAddModalOpen] = React.useState(false);
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<any>(null);

    const [filter, setFilter] = useState('');

    const debounceTimer = useRef<NodeJS.Timeout>();
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            gridRef.current?.onRefresh();
        }, 300);
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    }, [filter]);

    const renderToolbar = useCallback(
        () => (
            <PermitGridToolbar
                filter={filter}
                setFilter={setFilter}
                handleRefresh={() => gridRef.current?.onRefresh()}
                placeholder="Search design packages"
            />
        ),
        [filter, setFilter]
    );

    const filterList = [
        { id: 'packageName', label: 'Package Name', dataType: 'string', defaultValue: '' },
        { id: 'packagePrice', label: 'Price ($)', dataType: 'number', defaultValue: '' },
        { id: 'noPermitDiscount', label: 'No Permit Discount ($)', dataType: 'number', defaultValue: '' },
    ];

    const headCells = [
        { field: 'packageName', headerName: 'Package Name', sortable: true, flex: 2 },
        {
            field: 'packagePrice',
            headerName: 'Price ($)',
            type: 'number',
            valueFormatter: (value) => {
                const numValue = Number(value);
                if (isNaN(numValue) || value == null) return '$0.00';
                return numValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            },
            flex: 1,
        },
        {
            field: 'noPermitDiscount',
            headerName: 'No Permit Discount ($)',
            type: 'number',
            valueFormatter: (value) => {
                const numValue = Number(value);
                if (isNaN(numValue) || value == null) return '$0.00';
                return numValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            },
            flex: 1,
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key="edit"
                    label="Edit"
                    onClick={() => { setEditModalOpen(true); setSelectedRow(row); }}
                    showInMenu
                />,
                <GridActionsCellItem
                    key="delete"
                    label="Delete"
                    onClick={() => { setDeleteModalOpen(true); setSelectedRow(row); }}
                    showInMenu
                />,
            ],
            flex: 0.5,
        },
    ];

    return (
        <Box display="flex" flexDirection="column" maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }} height="100%">
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.default',
                    zIndex: 10,
                    pb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack spacing={1}>
                        <Typography variant="h2" fontWeight={700}>
                            Design Packages
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            Create design packages that can be applied to quotes
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="flex-end">
                        {renderToolbar()}
                        <Button variant="contained" color="success" onClick={() => setAddModalOpen(true)}>
                            Add New Package
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <AnyGrid
                    ref={gridRef}
                    headCells={headCells}
                    filterList={filterList}
                    searchEndpont={`${config.uri.base}/logisticCosts/designPackages`}
                    searchAdditionalQueryParams={logisticCostsId ? { logisticCostsId, ...(filter ? { packageName: filter } : {}) } : {}}
                    pageSizeOptions={[10, 25, 50]}
                    selectableRows={false}
                    hybridMode={true}
                    initialPageSize={1000}
                    customNoRowsOverlay={EmptyStateComponent}
                />
            </Box>

            {addModalOpen && (
                <AddDesignPackageModal
                    gridRef={gridRef}
                    modalOpen={addModalOpen}
                    setModalOpen={setAddModalOpen}
                />
            )}
            {editModalOpen && selectedRow && (
                <EditDesignPackageModal
                    rowData={selectedRow}
                    gridRef={gridRef}
                    modalOpen={editModalOpen}
                    setModalOpen={setEditModalOpen}
                />
            )}
            {deleteModalOpen && selectedRow && (
                <DeleteDesignPackageModal
                    rowData={selectedRow}
                    gridRef={gridRef}
                    modalOpen={deleteModalOpen}
                    setModalOpen={setDeleteModalOpen}
                />
            )}
        </Box>
    );
};
